import { Button } from "@project-mgmt/ui";

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  showRetry?: boolean;
  className?: string;
}

export function ErrorState({
  title = "Something went wrong",
  message = "An error occurred while loading this content. Please try again.",
  onRetry,
  showRetry = true,
  className = "",
}: ErrorStateProps) {
  return (
    <div className={`py-8 text-center ${className}`}>
      <svg className="mx-auto mb-4 h-12 w-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
        />
      </svg>
      <h3 className="mb-2 text-lg font-medium text-black">{title}</h3>
      <p className="mx-auto mb-4 max-w-md text-gray-600">{message}</p>
      {showRetry && onRetry && (
        <Button onClick={onRetry} variant="outline">
          Try Again
        </Button>
      )}
    </div>
  );
}

interface NetworkErrorStateProps {
  onRetry?: () => void;
  className?: string;
}

export function NetworkErrorState({ onRetry, className = "" }: NetworkErrorStateProps) {
  return (
    <ErrorState
      title="Connection Error"
      message="Unable to connect to the server. Please check your internet connection and try again."
      onRetry={onRetry}
      className={className}
    />
  );
}

interface NotFoundState {
  title?: string;
  message?: string;
  onGoBack?: () => void;
  className?: string;
}

export function NotFoundState({
  title = "Not Found",
  message = "The content you're looking for doesn't exist or has been moved.",
  onGoBack,
  className = "",
}: NotFoundState) {
  return (
    <div className={`py-8 text-center ${className}`}>
      <svg className="mx-auto mb-4 h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.562M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
      <h3 className="mb-2 text-lg font-medium text-black">{title}</h3>
      <p className="mx-auto mb-4 max-w-md text-gray-600">{message}</p>
      {onGoBack && (
        <Button onClick={onGoBack} variant="outline">
          Go Back
        </Button>
      )}
    </div>
  );
}

interface EmptyStateProps {
  title?: string;
  message?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  icon?: React.ReactNode;
  className?: string;
}

export function EmptyState({
  title = "No data available",
  message = "There's nothing to show here yet.",
  action,
  icon,
  className = "",
}: EmptyStateProps) {
  const defaultIcon = (
    <svg className="mx-auto mb-4 h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );

  return (
    <div className={`py-8 text-center ${className}`}>
      {icon || defaultIcon}
      <h3 className="mb-2 text-lg font-medium text-black">{title}</h3>
      <p className="mx-auto mb-4 max-w-md text-gray-600">{message}</p>
      {action && <Button onClick={action.onClick}>{action.label}</Button>}
    </div>
  );
}
